import type { SupabaseClient } from '@supabase/supabase-js';
import type { BoardStore, FeedProvenanceStore } from '../ports.js';
import type {
  ActivityOverride,
  ActivityQualification,
  BoardActivity,
  BoardDay,
  CleaningRow,
  ServiceGroupRoom,
  ServiceListRow,
} from '../boardCoverage.js';
import type { ActivityPresence, BoardAbsence } from '../activityBoard.js';
import {
  rowToAbsence,
  rowToBoardActivity,
  rowToBoardDay,
  rowToOverride,
  rowToPresence,
  rowToQualification,
} from '../boardRows.js';

/**
 * `BoardStore` over PostgREST, using the BROWSER's authed client under RLS.
 *
 * NOT a service-role adapter. The client is injected by each app's composition
 * root, so the caller's own policies apply exactly as they did when these
 * queries were inline in the components — this is a pure relocation of the read
 * path, not a change of privilege.
 *
 * THE COLUMN LISTS LIVE HERE AND NOWHERE ELSE. Before ladder step 6 they were
 * copy-pasted across the views (`PRESENCE_COLUMNS` in three files,
 * `OVERRIDE_COLUMNS` in three, `ABSENCE_COLUMNS` in two), so a column rename
 * meant a component sweep and the schema was effectively part of the UI's
 * contract. Explicit column lists are also mandatory rather than stylistic:
 * `staff_employees` carries COLUMN grants (migration 0016), so `select('*')`
 * is a permission error for a staff-client read.
 */

// EXPORTED so `boardStore.test.ts` can pin each list against the row type it
// feeds — a column list is the one part of this adapter no unit test exercises
// (PostgREST 400s on an unknown column, but only against a real database).
// That gap was not theoretical: the step-6 draft of ACTIVITY_COLUMNS carried
// three columns `board_activities` does not have (`required`, `threshold`,
// `is_active`) and omitted four it does, and survived because `activities()`
// had no caller until Planning was migrated.
export const DAY_COLUMNS =
  'date,departures,arrivals,stayovers,occupancy,breakfasts,covers,half_boards,diners,note,updated_at,' +
  // 0146 — the restaurant row. Same rule as below: this list and the
  // migration move together or reads stop working.
  'diners_hotel,diners_external_booked,packed_lunches,' +
  // 0129 (M3) — the people dimension. PostgREST 400s on an unknown column,
  // so this list and the migration move together or reads stop working.
  'guests_adults,guests_children,arrivals_guests,departures_guests';
export const ACTIVITY_COLUMNS =
  'id,label,window_start,window_end,window_note,demand_driver,demand_formula,min_demand,active,sort,team,untimed,load_metric_key,badge_metric_key';
export const OVERRIDE_COLUMNS = 'id,activity_id,date,window_start,window_end,required,note,updated_at';
export const PRESENCE_COLUMNS = 'activity_id,employee_id,date,status,source,declared_by,message_id';
export const ABSENCE_COLUMNS = 'employee_id,date,source,declared_by';

/** PostgREST returns `{ data, error }`; a read failure is not recoverable here,
 *  so it surfaces as a throw the calling view already handles. */
function unwrap<T>(r: { data: T[] | null; error: { message: string } | null }, what: string): T[] {
  if (r.error) throw new Error(`${what}: ${r.error.message}`);
  return r.data ?? [];
}

export class SupabaseBoardStore implements BoardStore {
  constructor(private readonly db: SupabaseClient) {}

  async days(from: string, to: string): Promise<BoardDay[]> {
    const r = await this.db
      .from('board_days')
      .select(DAY_COLUMNS)
      .gte('date', from)
      .lte('date', to)
      .order('date');
    return unwrap(r, 'board_days').map((row) => rowToBoardDay(row as never));
  }

  async occupancyOn(date: string): Promise<number | null> {
    const { data, error } = await this.db
      .from('board_days')
      .select('occupancy')
      .eq('date', date)
      .maybeSingle();
    if (error) throw new Error(`board_days.occupancy: ${error.message}`);
    return (data as { occupancy: number | null } | null)?.occupancy ?? null;
  }

  /** `board_service_list` returns pre-formatted text — see `ServiceListRow`. */
  async serviceList(date: string): Promise<ServiceListRow[]> {
    const r = await this.db.rpc('board_service_list', { p_date: date });
    if (r.error) throw new Error(`board_service_list: ${r.error.message}`);
    const rows = (r.data ?? []) as {
      service: string;
      party: string;
      room: string;
      rooms: string[] | null;
      stay_type: string;
      stay_from: string | null;
      stay_to: string | null;
      guests: number;
    }[];
    return rows.map((row) => ({
      service: row.service === 'breakfast' ? 'breakfast' : 'dinner',
      party: row.party,
      room: row.room,
      // An empty array and « no rooms » are the same thing to every caller, and
      // PostgREST can send either; normalised here so no renderer has to test
      // for both.
      rooms: row.rooms && row.rooms.length > 0 ? row.rooms : null,
      stayType: row.stay_type,
      stayFrom: row.stay_from,
      stayTo: row.stay_to,
      guests: row.guests,
    }));
  }

  async serviceGroupRooms(date: string): Promise<ServiceGroupRoom[]> {
    const r = await this.db.rpc('board_service_group_rooms', { p_date: date });
    if (r.error) throw new Error(`board_service_group_rooms: ${r.error.message}`);
    const rows = (r.data ?? []) as {
      resa: string;
      group_name: string;
      room: string;
      floor: number;
      pdj: number;
      effectif_total: number | null;
    }[];
    return rows.map((row) => ({
      resa: row.resa,
      groupName: row.group_name,
      room: row.room,
      floor: row.floor,
      pdj: row.pdj,
      effectifTotal: row.effectif_total,
    }));
  }

  async cleaningList(date: string): Promise<CleaningRow[]> {
    const r = await this.db.rpc('board_cleaning_list', { p_date: date });
    if (r.error) throw new Error(`board_cleaning_list: ${r.error.message}`);
    const rows = (r.data ?? []) as {
      section: string;
      room: string;
      floor: number;
      guests: number | null;
      convertible: boolean;
      turnover: boolean;
      double_bed_type: string | null;
      arrangements: { code: string; doubles: number; singles: number }[] | null;
      current_config: string | null;
      requested_config: string | null;
    }[];
    return rows.map((row) => ({
      section:
        row.section === 'ablancs' || row.section === 'recouches' ? row.section : 'arrivees',
      room: row.room,
      floor: row.floor,
      guests: row.guests,
      convertible: row.convertible,
      turnover: row.turnover,
      doubleBedType: row.double_bed_type,
      // A jsonb array now, so a room the PMS does not know is ONE empty value
      // downstream rather than five columns that have to be tested together.
      arrangements: row.arrangements ?? [],
      // No resolution step: the database constrains the standing arrangement to
      // one the room can take (0174), so the CODE is the answer. 0157 had to
      // match it against two layouts by bed count.
      currentConfig: row.current_config,
      requestedConfig: row.requested_config,
    }));
  }

  async activities(): Promise<BoardActivity[]> {
    const r = await this.db.from('board_activities').select(ACTIVITY_COLUMNS).order('sort');
    return unwrap(r, 'board_activities').map((row) => rowToBoardActivity(row as never));
  }

  async qualifications(): Promise<ActivityQualification[]> {
    const r = await this.db.from('board_activity_qualifications').select('activity_id,employee_id,is_main');
    return unwrap(r, 'board_activity_qualifications').map((row) => rowToQualification(row as never));
  }

  async overrides(from: string, to: string): Promise<ActivityOverride[]> {
    const r = await this.db
      .from('board_activity_overrides')
      .select(OVERRIDE_COLUMNS)
      .gte('date', from)
      .lte('date', to);
    return unwrap(r, 'board_activity_overrides').map((row) => rowToOverride(row as never));
  }

  async presence(from: string, to: string): Promise<ActivityPresence[]> {
    const r = await this.db
      .from('board_activity_presence')
      .select(PRESENCE_COLUMNS)
      .gte('date', from)
      .lte('date', to);
    return unwrap(r, 'board_activity_presence').map((row) => rowToPresence(row as never));
  }

  async absences(from: string, to: string): Promise<BoardAbsence[]> {
    const r = await this.db
      .from('board_absence')
      .select(ABSENCE_COLUMNS)
      .gte('date', from)
      .lte('date', to);
    return unwrap(r, 'board_absence').map((row) => rowToAbsence(row as never));
  }
}

/**
 * One question: when were the board's figures last true? (METRICS-FEED-BRIEF
 * §6.2, RUNBOOK §20.7.) The ACL over C4's audit table — it returns no Vega
 * concept, so a future PMS feed answers through the same port.
 *
 * ── It reads an RPC, not the table, and that is the whole point (0132) ──
 *
 * It used to `select` from `vega_import` directly. That table's only policy
 * sits on `pms.ops.read`, which is operator-only, so `reception.*` matched no
 * rows — and RLS filters SILENTLY, so "you may not ask" arrived as an empty
 * result, i.e. as `null`, i.e. as « aucun import »: the desk read a healthy
 * feed as a dead one while bos.* showed « importé à 16:03 ».
 *
 * `board_feed_provenance()` is gated on `rh.board.read` — whoever may read the
 * figures may read their freshness — and RAISES rather than returning nothing,
 * so a refusal now surfaces here as a throw and renders as « import inconnu ».
 * The two ordered reads (never advance the stamp onto a refused attempt) moved
 * into the function with it, where both writers can see the same rule.
 */
export class SupabaseFeedProvenanceStore implements FeedProvenanceStore {
  constructor(private readonly db: SupabaseClient) {}

  async latest(): Promise<{ importedAt: string; applied: boolean; refusalReason: string | null } | null> {
    const r = await this.db.rpc('board_feed_provenance').maybeSingle();
    if (r.error) throw new Error(`board_feed_provenance: ${r.error.message}`);

    const row = r.data as { imported_at: string; applied: boolean; refusal_reason: string | null } | null;
    // No row is now unambiguous: a caller who may not ask gets an ERROR above,
    // which the component renders as « import inconnu ». Only an empty
    // `vega_import` reaches here, and that genuinely is "never ran".
    return row
      ? { importedAt: row.imported_at, applied: row.applied, refusalReason: row.refusal_reason }
      : null;
  }
}
